import React, { useMemo, useState } from "react";
import { Box, Container } from "@mui/material";
import { useFetchDetail } from "@hooks/useFetchDetail";
import { useLocation, useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import HeroCst from "@components/HeroCst";
import BackgroundImageCst from "@components/BackgroundImageCst";
import Breadcrumb from "@components/Breadcrumb";
import { useTranslation } from "react-i18next";
import ActivityFormCst from "../ActivityFormCst";
import { ServicesURL, downloadFile } from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import { isTrueValue } from "@utils/utilsValidators";
import useSWRMutation from "swr/mutation";
import { MethodType } from "src/services/type";
import { fetcher } from "src/services/config";
import { StatusEnum } from "@components/ModalConfirmsCst/types";
import ModalConfirmsCst from "@components/ModalConfirmsCst";
import AnimationFadeIn from "@components/AnimationFadeIn";
import { useEditActivity } from "@hooks/hookscatalog/Activity/useEditActivity";
import { ColumnInterface } from "@components/Table/types";
import { useAuth } from "@context/auth";

const EditActivities: React.FC<{
  duplication?: boolean;
}> = ({ duplication = false }) => {
  const { user } = useAuth();
  const { t } = useTranslation(["activities-page"]);
  const { idLO, id } = useParams();
  const navigate = useNavigate();

  const readOnly =
    (user?.permissions.includes("learning_catalogue.learning_object.read") &&
      !user?.permissions.includes(
        "learning_catalogue.learning_object.write",
      )) ??
    false;

  const {
    onSubmit,
    openToast,
    setOpenToast,
    columns,
    dataDetail,
    isValidatingDetail,
  } = useEditActivity(PagesID["catalog.activities"], id, idLO, duplication);

  const formatData = useMemo(() => {
    const formatArr = { ...dataDetail };
    if (formatArr && Object.keys(formatArr).length > 0) {
      if (!!duplication) {
        return {
          ...dataDetail,
          publish_date: null,
          title: `${dataDetail.title} - Copied`,
        };
      } else {
        return { ...dataDetail };
      }
    } else {
      return {};
    }
  }, [dataDetail]);

  return (
    <AnimationFadeIn>
      <BackgroundImageCst
        customWidth={"100%"}
        customHeight={"603px"}
        image="section-activities.jpg"
        position={"absolute"}
        fullpage={true}
      />
      <Breadcrumb />
      <Container
        maxWidth={"xxl"}
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        <HeroCst
          title={t(`edit.hero-title${duplication ? "-duplicate" : ""}`)}
          description={t("edit.hero-description")}
        />
        <Box
          sx={{
            mt: "25px",
          }}
        >
          <ActivityFormCst
            row={formatData}
            readOnly={readOnly}
            columns={
              columns &&
              columns
                .map((col: any) => {
                  if (col.type === "upload" && dataDetail[col.accessorKey]) {
                    const { label, link } = dataDetail[col.accessorKey];
                    return {
                      ...col,
                      downloadCallback: link
                        ? () => {
                            downloadFile(link, false, label);
                          }
                        : null,
                    };
                  }
                  return col;
                })
                .filter((col: any) => {
                  if (duplication) {
                    return ![
                      "actual_package_version",
                      "available_package_version",
                      "update_package",
                    ].includes(col.accessorKey);
                  } else {
                    return true;
                  }
                })
            }
            onSubmit={onSubmit}
            backButton={() => {
              navigate(`/corporate/${id}/detail/catalog/activities`);
            }}
            duplication={duplication}
            edit
            idEntity={idLO}
            isValidatingDetail={isValidatingDetail}
          />
        </Box>
      </Container>
      <ModalConfirmsCst
        open={!!openToast}
        title={t("hero-title-add")}
        description={""}
        onCallBackClosed={() => {
          setOpenToast(undefined);
        }}
        status={openToast}
      />
    </AnimationFadeIn>
  );
};

export default EditActivities;
